import { useEffect, useState } from "react";

import { GenericResponse } from "@/Common/Api/Dto/GenericResponse";
import { ISaveManualResultsRequest } from "@/Common/Api/ManualResults/Dtos/ISaveManualResultsRequest";
import { LabObservationDto } from "@/Common/Api/ManualResults/Dtos/LabObservationsDto";
import { ManualResultsApiClient } from "@/Common/Api/ManualResults/ManualResultsApiClient";
import { CliniciansHelper } from "@/Common/Helpers/CliniciansHelper";
import { EmployeeTitleHelper } from "@/Common/Helpers/EmployeeTitleHelper";
import { ManualLabResultsEntryFormModel } from "@/Common/Models/ManualLabResultsEntry/ManualLabResultsEntryFormModel";
import { Person } from "@/Common/Models/Person/Person";
import { ButtonControl } from "../Buttons/ButtonControl";
import { Modal } from "../Modals/Modal";
import { ClinicianSearchStrategy } from "../Search/PersonSearchAutocompleteCustom";
import { ManualLabResultsEntry } from "./ManualLabResultsEntry";

import styles from "./ManualResultsFormPopup.module.scss";

interface ManualResultsFormPopupProps {
    readonly currentPatient: Person;
    readonly onClose: () => void;
}

interface ManualResultsPopupFooterProps {
    onClose: () => void;
    onSave: () => void;
    onSaveAndFile: () => void;
    enableSaveButton: boolean;
}

function ManualResultsPopupFooter(props: Readonly<ManualResultsPopupFooterProps>): JSX.Element {
    return (
        <div className={styles.footer}>
            <ButtonControl
                onClick={props.onClose}
                dataCy="manual-lab-results-cancel-button"
                label={cacheStore.GetString("CancelLabel")}
                variant="neutral"
            />
            {(EmployeeTitleHelper.isDoctor(sessionStore.myleEmployeeTitle) || EmployeeTitleHelper.isStudent(sessionStore.myleEmployeeTitle)) && (
                <ButtonControl
                    onClick={props.onSave}
                    dataCy="manual-lab-results-save-button"
                    label={cacheStore.GetString("SaveLabel")}
                    variant="brand"
                    disabled={!props.enableSaveButton}
                />
            )}
            {EmployeeTitleHelper.isDoctor(sessionStore.myleEmployeeTitle) && (
                <ButtonControl
                    onClick={props.onSaveAndFile}
                    dataCy="manual-lab-results-save-button"
                    label={cacheStore.GetString("SavePlusFileLabel")}
                    variant="brand"
                    disabled={!props.enableSaveButton}
                />
            )}
        </div>
    );
}

function ManualResultsFormPopup({ currentPatient, onClose }: ManualResultsFormPopupProps): JSX.Element {
    const [data, setData] = useState<ManualLabResultsEntryFormModel>(ManualLabResultsEntryFormModel.default());
    const [clinicianSearchStrategy, setClinicianSearchStrategy] = useState<ClinicianSearchStrategy>(null);

    useEffect(() => {
        async function fetchClinicianSearchStrategy(): Promise<void> {
            const strategy = await CliniciansHelper.getPatientAndAllCliniciansSearchStrategy(currentPatient);
            setClinicianSearchStrategy(strategy);
        }

        fetchClinicianSearchStrategy();
    }, [currentPatient]);

    const handleSave = async (): Promise<void> => {
        const response = await ManualResultsApiClient.saveManualResult(prepareRequest());
        handleResponse(response);
    };

    const handleClose = (): void => {
        setData(ManualLabResultsEntryFormModel.default());
        onClose();
    };

    const areAllValid = data.observations.every(observation => observation.isValid()) && data.addtionalInformationData.isValid();

    const handleSaveAndFile = async (): Promise<void> => {
        const response = await ManualResultsApiClient.saveManualResult(prepareRequest(true));
        handleResponse(response);
    };

    const handleResponse = (response: GenericResponse): void => {
        if (response.IsSuccess)
            handleClose();
        else
            sessionStore.showPopup(response.Errors);
    };

    const prepareRequest = (shouldFile: boolean = false): ISaveManualResultsRequest => ({
        CarbonCopies: data.addtionalInformationData.cc?.map(cc => cc.ContactFacetId),
        Origin: data.addtionalInformationData.origin,
        Prescribers: data.addtionalInformationData.prescribers?.map(cc => cc.ContactFacetId),
        RequestNo: data.addtionalInformationData.requestNo,
        Observations: data.observations.map<LabObservationDto>(observation => ({
            Analysis: `${observation.AnalysisModel.DescriptionShort} -  ${observation.analysis}`,
            CategoryLookupId: observation.categoryLookupId,
            DateTime: observation.dateTime,
            Id: observation.id,
            ReferenceHigh: observation.referenceHigh,
            ReferenceLow: observation.referenceLow,
            Result: observation.result,
            Units: observation.units,
            Level: observation.level,
            Note: observation.note
        })),
        PatientContactFacetId: currentPatient.ContactFacetId,
        PatientContactId: currentPatient.ContactId,
        ShouldFile: shouldFile,
        ResultSentDateTime: data.addtionalInformationData.resultSentDateTime
    } as ISaveManualResultsRequest);

    return (
        <Modal
            onClose={handleClose}
            title={cacheStore.GetString("ManualResults_ManualLabResultsLabel")}
            showX
            draggable
            top={60}
            dataCy="manualLabResults-modal"
            cssClass={styles.manualResultsPopup}
            footer={<ManualResultsPopupFooter onClose={handleClose} onSave={handleSave} enableSaveButton={areAllValid} onSaveAndFile={handleSaveAndFile} />}
        >
            <div className={styles.manualResultsPopupBody}>
                <ManualLabResultsEntry
                    data={data}
                    onChange={data => setData(data)}
                    clinicianSearchStrategy={clinicianSearchStrategy}
                />
            </div>
        </Modal>
    );
}

export { ManualResultsFormPopup };
